import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Autocomplete, Button, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import EditPopup from '../../../academicCalendar/calendarEdit/EditPopup';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import {
  getBatchCalendarEdit,
  getSubjectsUnderBatchId,
  putBatchCalendarEdit,
  putBulkBatchCalendarEdit,
} from '../../../../redux/features/batchSlice';

export default function BatchCalenderEdit() {
  const { id } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const dispatch = useDispatch();
  const [subId, setSubId] = useState(null);

  const [change, setChange] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    loading,
    subjects: Subjects,
    batchCalenderEdit: { tableData, loading: tableLoading },
  } = useSelector((state) => state.batch);

  useEffect(() => {
    if (id) {
      dispatch(getSubjectsUnderBatchId(id));
    }
  }, [id]);

  const dispatchParams = useMemo(
    () => ({
      batch_id: id,
      subject_enrollment_id: subId,
    }),
    [subId, id],
  );

  useEffect(() => {
    dispatch(getBatchCalendarEdit(dispatchParams));
  }, [subId, change]);

  const tableHead = useMemo(() => [
    {
      field: 'sl_no',
      headerName: 'Sl No',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'subject_name',
      headerName: 'Subject',
      width: 180,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'session_date',
      headerName: 'Session Date',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'day_name',
      headerName: 'Day',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'reschedule_requested_by_name',
      headerName: 'Reschedule Requested',
      width: 180,
      headerAlign: 'center',
      align: 'center',
    },

    {
      field: 'from_time',
      headerName: 'From Time',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'to_time',
      headerName: 'To Time',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'isEditable',
      headerName: 'Action',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <Tooltip title={row?.message}>
          <span>
            <Button disabled={Boolean(row?.trainerAttendanceID)} variant="contained" onClick={() => setOpen(row)}>
              Edit
            </Button>
          </span>
        </Tooltip>
      ),
    },
  ]);

  return (
    <GlobalWrapper title={'Calendar Edit'}>
      <Stack gap={3}>
        <GlobalDialog
          open={Boolean(open)}
          setOpen={setOpen}
          title={Array.isArray(open) ? 'Bulk Edit' : 'Edit'}
          content={
            <EditPopup
              row={open}
              loading={loading}
              setChange={setChange}
              setOpen={setOpen}
              dispatchFn={Array.isArray(open) ? putBulkBatchCalendarEdit : putBatchCalendarEdit}
              setSelectedRows={setSelectedRows}
            />
          }
          closeButton={true}
        />
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Autocomplete
            loading={loading}
            getOptionLabel={({ subject_name }) => subject_name || ''}
            getOptionKey={({ subject_enrollment_id }) => subject_enrollment_id || null}
            isOptionEqualToValue={(option, value) => option?.subject_enrollment_id === value?.subject_enrollment_id}
            options={[{ subject_name: 'All', subject_enrollment_id: null }, ...Subjects]}
            value={
              subId === null
                ? { subject_name: 'All', subject_enrollment_id: null }
                : Subjects?.find((e) => e.subject_enrollment_id === subId || '')
            }
            onChange={(_, v) => {
              setSubId(v?.subject_enrollment_id || null);
            }}
            sx={{ width: 300 }}
            renderInput={(params) => <TextField {...params} label="Subject" />}
          />
          <Button
            variant="outlined"
            color="info"
            disabled={!Boolean(selectedRows?.length)}
            onClick={() => {
              setOpen(selectedRows);
            }}
          >
            Bulk Edit
          </Button>
        </Stack>
        <DataGrid
          checkboxSelection
          columns={tableHead}
          rows={tableData}
          getRowId={({ session_schedule_id }) => session_schedule_id}
          autoHeight
          loading={tableLoading}
          selectionModel={selectedRows}
          onSelectionModelChange={setSelectedRows}
          isRowSelectable={(params) => !Boolean(params.row?.trainerAttendanceID)}
          disableSelectionOnClick
        />
      </Stack>
    </GlobalWrapper>
  );
}
